import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MenuIcon, SearchIcon, ShoppingBagIcon, StarIcon, XIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { mainNav, usps } from '../../data/navigation';
import { Logo } from '../brand/Logo';
import { SearchBox } from './SearchBox';
import { MobileMenu } from './MobileMenu';
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const {
    count,
    open
  } = useCart();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [usp, setUsp] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => setUsp(u => (u + 1) % usps.length), 4000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => setSearchOpen(false), [pathname]);
  const navItems = mainNav.filter(n => n.to !== '/collectie/alles');
  const linkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `whitespace-nowrap py-1 text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors duration-150 ${isActive ? 'text-ink underline decoration-2 underline-offset-[10px]' : 'text-ink/70 hover:text-ink'}`;
  return <>
      <header className="sticky top-0 z-40 bg-canvas" data-id="element-216" data-mp="3k2gm9fy0fkra">
        <div className="bg-berry text-white" data-id="element-217" data-mp="3k3rt5omr58mm">
          <div className="mx-auto grid h-9 max-w-7xl grid-cols-1 items-center px-5 text-xs sm:grid-cols-[1fr_auto_1fr] lg:px-8" data-id="element-218" data-mp="3p11lpd5fu0eo">
            <div className="hidden items-center gap-5 sm:flex" data-id="element-219" data-mp="3tzbi1r2wf71v">
              <span className="font-semibold uppercase tracking-[0.16em]" data-id="element-220" data-mp="1wc2zhvqe64x">Beauty</span>
              <Link to="/living" className="uppercase tracking-[0.16em] text-white/60 transition-colors duration-150 hover:text-white" data-id="element-221" data-mp="19a5pt82sixtj">
                Living
              </Link>
            </div>
            <div className="relative h-9 overflow-hidden text-center" aria-live="polite" data-id="element-222" data-mp="1ddlhdq0067zd">
              <AnimatePresence mode="wait" initial={false} data-id="element-223" data-mp="3ae7wosjizu1d">
                <motion.p key={usp} initial={reduce ? false : {
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={reduce ? undefined : {
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.2,
                ease
              }} className="flex h-9 items-center justify-center whitespace-nowrap font-medium" data-id="element-224" data-mp="35cihr1t6mw80">
                  {usps[usp]}
                </motion.p>
              </AnimatePresence>
            </div>
            <p className="hidden items-center justify-end gap-1.5 md:flex" data-id="element-225" data-mp="30fs38ix108op">
              <StarIcon className="h-3 w-3 fill-white" strokeWidth={0} aria-hidden="true" data-id="element-226" data-mp="2vmfr389j9ucu" />
              4,8 · 3.100+ reviews
            </p>
          </div>
        </div>

        <div className="border-b border-line" data-id="element-227" data-mp="22z32ytnq5ehf">
          <div className="mx-auto grid h-16 max-w-7xl grid-cols-[1fr_auto_1fr] items-center px-5 lg:h-20 lg:px-8" data-id="element-228" data-mp="22ya5ykeki3ch">
            <div className="flex items-center gap-1" data-id="element-229" data-mp="ieo83wy39l4w">
              <button type="button" onClick={() => setMenuOpen(true)} className="-ml-2 grid h-10 w-10 place-items-center lg:hidden" aria-label="Menu openen" data-id="element-230" data-mp="n7r8anxdfsu2">
                <MenuIcon className="h-5 w-5" data-id="element-231" data-mp="9e9or34oqg41" />
              </button>
              <button type="button" onClick={() => setSearchOpen(s => !s)} className="grid h-10 w-10 place-items-center lg:hidden" aria-label={searchOpen ? 'Zoeken sluiten' : 'Zoeken'} aria-expanded={searchOpen} data-id="element-232" data-mp="efpa6waryomy">
                {searchOpen ? <XIcon className="h-5 w-5" data-id="element-233" data-mp="s8ybe9okk3ue" /> : <SearchIcon className="h-5 w-5" data-id="element-234" data-mp="x55iw6wm5neg" />}
              </button>
              <div className="hidden w-full max-w-[260px] lg:block" data-id="element-235" data-mp="3cwt4x7a39vih">
                <SearchBox data-id="element-236" data-mp="3cvi0d58tkjqb" />
              </div>
            </div>

            <Link to="/" aria-label="AMIRA Beauty, naar home" data-id="element-237" data-mp="3hw5zyfb468w5">
              <Logo data-id="element-238" data-mp="1057xk1cmhnp" />
            </Link>

            <div className="flex justify-end" data-id="element-239" data-mp="116scf498gi3">
              <button type="button" onClick={open} className="flex h-10 items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em]" aria-label={`Winkelwagen, ${count} artikelen`} data-id="element-240" data-mp="11zut3w6kjuf">
                <span className="relative" data-id="element-241" data-mp="3jfpftroy1s0m">
                  <ShoppingBagIcon className="h-5 w-5" aria-hidden="true" data-id="element-242" data-mp="jyr6os9e7wog" />
                  <span className="absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-pink px-1 text-[10px] font-semibold tabular-nums text-white" data-id="element-243" data-mp="jwokykf9wppj">
                    {count}
                  </span>
                </span>
                <span className="hidden sm:inline" data-id="element-244" data-mp="5y6pu1n1gaxl">Winkelwagen</span>
              </button>
            </div>
          </div>

          <nav aria-label="Shop" className="hidden border-t border-line lg:block" data-id="element-245" data-mp="2vq4vp5h20opr">
            <ul className="mx-auto flex h-12 max-w-7xl items-center justify-center gap-9 px-8" data-id="element-246" data-mp="2qopddl69gwb2">
              {navItems.map(item => <li key={item.to} data-id="element-247" data-mp="2gjpe16nhgir4">
                  <NavLink to={item.to} className={linkClass} data-id="element-248" data-mp="2ljtxn5jottlk">
                    {item.label}
                  </NavLink>
                </li>)}
              <li data-id="element-249" data-mp="2qhvfjn5dnmvg">
                <NavLink to="/over-amira" className={linkClass} data-id="element-250" data-mp="2va6g8s7xzcp2">
                  Ons verhaal
                </NavLink>
              </li>
              <li data-id="element-251" data-mp="2zb8g9b9onss3">
                <Link to="/living" className="whitespace-nowrap py-1 text-[13px] font-semibold uppercase tracking-[0.12em] text-pink transition-colors duration-150 hover:text-berry" data-id="element-252" data-mp="2zbs0iymt2t9a">
                  AMIRA Living
                </Link>
              </li>
            </ul>
          </nav>

          <AnimatePresence data-id="element-253" data-mp="3e8sgy5zjr1sg">
            {searchOpen && <motion.div initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18,
            ease
          }} className="border-t border-line bg-canvas lg:hidden" data-id="element-254" data-mp="1ewq0l4m7ynj0">
                <div className="px-5 py-3" data-id="element-255" data-mp="16w0m9bol0pv1">
                  <SearchBox autoFocus onNavigate={() => setSearchOpen(false)} data-id="element-256" data-mp="16xbk6uvcj3e8" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </header>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} data-id="element-257" data-mp="1gsda3sxg49hv" />
    </>;
}